import { Node, Edge } from "@xyflow/react"

export type ExecutionStatus = "completed" | "failed" | "skipped" | "pending"

/**
 * Injected UI-only fields used to render the inline "+" button on
 * terminal (unconnected) source handles and execution status overlays.
 * These are populated by the canvas and read by each node component —
 * never persisted.
 */
export interface NodeDisplayExtras {
  _terminalHandles?: string[]
  _onAddAtTerminal?: (nodeId: string, handleId: string) => void
  /** Handle ID of the terminal "+" currently being hovered by a dragged
   *  node. Used by the nodes to render a highlighted drop-target state. */
  _activeTerminalDropHandle?: string
  /** Per-run execution status of this node, set by the ExecutionCanvas
   *  when replaying a workflow execution. Undefined in the editor. */
  _executionStatus?: ExecutionStatus
  /** Error message from a failed execution — rendered below the node body. */
  _failedMessage?: string
}

export interface TriggerNodeData extends Record<string, unknown>, NodeDisplayExtras {
  nodeType: "TRIGGER"
  label: string
  triggerType: string
}

export const CHECK_KIND_LEAF = "leaf" as const
export const CHECK_KIND_GROUP = "group" as const

export const OPERATOR_AND = "AND" as const
export const OPERATOR_OR = "OR" as const

export type CheckGroupOperator = typeof OPERATOR_AND | typeof OPERATOR_OR

export interface CheckLeaf {
  kind: typeof CHECK_KIND_LEAF
  checkType: string
  comparison: string
  values: string[]
  endpoint?: string
  customData?: string
}

export interface CheckGroup {
  kind: typeof CHECK_KIND_GROUP
  operator: CheckGroupOperator
  children: CheckExpression[]
}

export type CheckExpression = CheckLeaf | CheckGroup

/** Sentinel checkType used on the condition node card when the node holds a
 *  multi-check expression. The serializer ignores the legacy fields and uses
 *  `expression` instead. */
export const GROUP_CHECK_TYPE = "GROUP"

export interface ConditionNodeData extends Record<string, unknown>, NodeDisplayExtras {
  nodeType: "CONDITION"
  label: string
  checkType: string
  comparison: string
  values: string[]
  endpoint?: string
  customData?: string
  /** Optional multi-check expression. When set, supersedes the leaf fields
   *  above and represents a tree of AND/OR grouped checks. */
  expression?: CheckExpression
}

export interface ActionNodeData extends Record<string, unknown>, NodeDisplayExtras {
  nodeType: "ACTION"
  label: string
  actionType: string
  reason?: string
  reasonDescription?: string
  customState?: string
  deliveryGiftCardProducts?: boolean
}

export type WorkflowNodeData = TriggerNodeData | ConditionNodeData | ActionNodeData

export type WorkflowNode = Node<WorkflowNodeData>
export type WorkflowEdge = Edge

/** A single string-value check used inside an ADDRESSES field. */
export interface ApiAddressStringCheck {
  comparison: string
  values: string[]
}

/** The per-address field checks (billing/shipping) inside an ADDRESSES check. */
export interface ApiAddressFields {
  email?: ApiAddressStringCheck
  givenName?: ApiAddressStringCheck
  familyName?: ApiAddressStringCheck
  telephoneNumber?: ApiAddressStringCheck
  streetAddress?: ApiAddressStringCheck
  streetAddress2?: ApiAddressStringCheck
  postalCode?: ApiAddressStringCheck
  city?: ApiAddressStringCheck
  stateOrProvince?: ApiAddressStringCheck
  country?: ApiAddressStringCheck
  pcc?: ApiAddressStringCheck
}

export interface ApiWorkflowCheck {
  type: string
  comparison?: string
  values?: (string | number)[]
  endpoint?: string
  customData?: Record<string, unknown>
  fields?: {
    billingAddress?: ApiAddressFields
    shippingAddress?: ApiAddressFields
    name?: ApiAddressStringCheck
    productParentId?: ApiAddressStringCheck
    productVariantId?: ApiAddressStringCheck
  }
  check1?: ApiWorkflowCheck
  check2?: ApiWorkflowCheck
}

export interface ApiWorkflowNode {
  type: string
  name: string
  then?: ApiWorkflowNode[]
  otherwise?: ApiWorkflowNode[]
  check?: ApiWorkflowCheck
  do?: {
    type: string
    reason?: { code: string; description: string }
    customState?: string
    selection?: { giftCardProducts?: boolean }
  }
  branches?: ApiWorkflowBranchCondition[]
}

export interface ApiWorkflowBranchCondition {
  type: string
  name: string
  check: ApiWorkflowCheck
  then?: ApiWorkflowNode[]
}
